import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";
import { BRAND } from "@/data/site";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/work", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/packages", label: "Packages" },
  { to: "/booth", label: "Booth" },
  { to: "/reviews", label: "Reviews" },
  { to: "/faq", label: "FAQ" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`sticky top-0 z-50 w-full backdrop-blur-xl transition-all duration-200 ${
        scrolled ? "bg-bde-cream/90 border-b border-bde-ink/10" : "bg-bde-cream/60"
      }`}
    >
      <div className="container-wide flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-bde-ink">
            <Heart className="h-4 w-4 fill-bde-pink stroke-bde-pink" />
          </span>
          <div className="leading-none">
            <div className="font-display text-xl tracking-tight font-bold">Best Day Ever</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-bde-pink">Photos &amp; Films</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors ${
                  isActive ? "text-bde-pink" : "text-bde-ink hover:text-bde-pink"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden md:inline-flex btn-primary !py-3 !px-5"
          >
            Check My Date
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center border-2 border-bde-ink"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-bde-ink/10 bg-bde-cream" data-testid="nav-mobile-menu">
          <div className="container-wide flex flex-col gap-1 py-4">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 font-display text-2xl ${isActive ? "text-bde-pink" : "text-bde-ink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary mt-3" data-testid="nav-mobile-cta">
              Check My Date
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
